import React, { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Container, Grid } from '@mui/material'
import { EmtButton, EmtGridContainer, EmtIcon, EmtTextarea, EmtTypography, EmtModal } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { useCart, useIsResponsive } from '@/hooks'
import { offerToService } from '@/hooks/useCart'
import { FiltersValues, getSearchAsync } from '@/modules/PetSitter/searchServiceHelper'
import { checkoutCartAsync } from '@/modules/Cart/CartServiceHelper'
import AddAnimalModal from '@/components/PetSitter/AddAnimal/AddAnimalModal'
import FloatingProfileModal from '@/components/FloatingProfileModal'

import AdditionalInformations, { AdditionalInformationsHandle } from './AdditionalInformations'

import AskSitterCard from '@/components/PetSitter/AskSitterCard'
import RequestSentModal from './RequestSentModal'
import SendMoreModal from './SendMoreModal'
import UserBox from '@/components/PetSitter/UserBox'
import AnimalBox from '@/components/PetSitter/AnimalBox'
import { petSitterProfileToUserBoxDTO } from './utils/petSitterUtils'
import { useFieldValidation } from './utils/validationUtils'
import { getProfileAsync } from '@/modules/PetSitter/viewProfileServiceHelper'
import useCurrentUser from '@/hooks/useCurrentUser'
import { PetsitterAvatar } from '@/components/PetSitter/PetsittersSelection/PetsitterAvatar'
import { PetsitterPrice } from '@/components/PetSitter/PetsittersSelection/PetsitterPrice'
import { EmptyPetsitterSlot } from '@/components/PetSitter/PetsittersSelection/EmptyPetsitterSlot'
import { useRouter } from 'next/router'
import { Routes } from '@/assets/configs'
import { InfoTextBox } from '@/components/PetSitter/ReservationCard/InfoTextBox'
import Link from 'next/link'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import { hasOffPlatformSignal } from '@/utils/messagingSafety/messagingSafety'
import SecurityModal from '@/components/pages/pet-sitter/reservations/messages/modals/SecurityModal'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { getReviews } from '@/modules/Reviews/ReviewsServiceHelper'

import {
  ServiceValues,
  services,
  serviceConfig,
  Contact,
  makeBlankSlot,
  checkHasService,
  isDateTimeInstanceIncomplete,
} from './requestConfig'

import { SERVICE_NAME_MAP, ServiceSlug, DateTimeInstance, getSlotType } from './requestConfig'

import { ServiceSection } from './ServiceSection'
import Constants from '@/assets/Constants'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'

interface Props {
  handleOpenProfile: ReturnType<typeof useFloatingProfile>['handleOpenProfile']
}

export type AdditionalInformations = {
  walkBehaviors: string[]
  indoorCleanliness?: 'never' | 'rare' | 'often'
  indoorBehaviors: string[]
  timeAlone?: string
  housing?: string
  summary?: string
}

/* -------------------------------------------------------------------------- */
/*                              NewRequest Component                          */
/* -------------------------------------------------------------------------- */

const useLocalStorageFilters = () => {
  const [filters, setFilters] = useState<any>(null)
  useEffect(() => {
    try {
      const storedFilters = localStorage.getItem('petSitter_filters')
      if (storedFilters) setFilters(JSON.parse(storedFilters))
    } catch (e) {
      console.error('Error reading filters from localStorage:', e)
    }
  }, [])
  return filters
}

export const isServiceRejectedReason = (reason?: string, selectedService?: string) => {
  if (!reason || !selectedService) return false
  return offerToService[reason as keyof typeof offerToService] === selectedService
}

const NewRequest: React.FC<Props> = ({ handleOpenProfile }) => {
  const t = useTranslation('petsitter').t
  const [isMobile, isTablet] = useIsResponsive()
  const [addContactOpen, setAddContactOpen] = useState(false)
  const [contactList, setContactList] = useState<'recommanded' | 'already-contacted'>('recommanded')
  const contactBoxRef = useRef<HTMLDivElement | null>(null)
  const [submittedContacts, setSubmittedContacts] = useState<Contact[]>([])
  const [formIsSend, setFormIsSend] = useState<boolean>(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const additionalInfosRef = useRef<AdditionalInformationsHandle | null>(null)
  const animalsBoxRef = useRef<HTMLDivElement | null>(null)

  const [showSelectedSittersModal, setShowSelectedSittersModal] = useState(false)

  const [requestSentInfo, setRequestSentInfo] = useState<React.ReactNode>(null)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)
  const [requestSentModalOpen, setRequestSentModalOpen] = useState<boolean>(false)
  const [sendMoreModalOpen, setSendMoreModalOpen] = useState<boolean>(false)
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false)
  const [pendingDescription, setPendingDescription] = useState<string>('')

  const selectedService = useNewRequestCartStore((s) => s.selectedService)
  const formSlotsByService = useNewRequestCartStore((s) => s.formSlotsByService)
  const animals = useNewRequestCartStore((s) => s.animals)
  const additionalInformations = useNewRequestCartStore((s) => s.additionalInformations)
  const message = useNewRequestCartStore((s) => s.message)

  const setSelectedService = useNewRequestCartStore((s) => s.setSelectedService)
  const setFormSlotsByService = useNewRequestCartStore((s) => s.setFormSlotsByService)
  const setAnimals = useNewRequestCartStore((s) => s.setAnimals)
  const updatePetToComplete = useNewRequestCartStore((s) => s.updatePetToComplete)
  const setAdditionalInformations = useNewRequestCartStore((s) => s.setAdditionalInformations)
  const addSlot = useNewRequestCartStore((s) => s.addSlot)
  const saveSlot = useNewRequestCartStore((s) => s.saveSlot)
  const removeSlot = useNewRequestCartStore((s) => s.removeSlot)

  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
  const addPetsitter = useNewRequestCartStore((s) => s.addPetsitter)
  const removePetsitter = useNewRequestCartStore((s) => s.removePetsitter)

  // 🔁 SLOTS: refs par slot.id
  const formRefs = useRef<Record<string, any>>({})

  const router = useRouter()
  const { checkField } = useFieldValidation()
  const { updateCart, serviceValues: cartServiceValues, rejectedPetsitters } = useCart()

  const localStorageFilters = useLocalStorageFilters()
  // Service initial (panier ou LS)
  // const initialService =
  //   Object.entries(cartServiceValues || {}).find(([_, instances]) => (instances as any[]).some((i) => i.offer))?.[0] ||
  //   (localStorageFilters?.services
  //     ? (serviceSlugMap as any)[localStorageFilters.services] || localStorageFilters.services
  //     : null)

  // useEffect(() => {
  //   if (initialService) setSelectedService(initialService)
  // }, [initialService])

  // LS → valeurs initiales des formulaires
  const getInitialValuesWithPeriod = (service: ServiceSlug) => {
    const baseValues = serviceConfig[service].initialValues
    if (!localStorageFilters?.period) return baseValues
    const { start, end } = localStorageFilters.period
    const startDate = start?.split('T')[0]
    const endDate = end?.split('T')[0]
    switch (service) {
      case 'promenade':
      case 'visite':
        return { ...baseValues, date: startDate }
      case 'garde-chez-vous':
      case 'garde-chez-eux':
        return { ...baseValues, dateStart: startDate, dateEnd: endDate }
      default:
        return baseValues
    }
  }

  const [localStorageInitialValues, setLocalStorageInitialValues] = useState<any>(
    selectedService && getSlotType(selectedService) ? getInitialValuesWithPeriod(getSlotType(selectedService)!) : null,
  )

  useEffect(() => {
    if (selectedService && getSlotType(selectedService)) {
      setLocalStorageInitialValues(getInitialValuesWithPeriod(getSlotType(selectedService)!))
    }
  }, [selectedService])

  // Valeurs de formulaire par service (description, animaux, instances dérivées des slots)
  const [serviceValues, setServiceValues] = useState<Record<ServiceSlug, ServiceValues>>({
    promenade: { description: message || '', animals: animals || [], dateTimeInstances: [] }, // ← no preload
    'garde-chez-vous': {
      description: message || '',
      animals: animals || [],
      dateTimeInstances: cartServiceValues?.['garde-chez-vous'] || [],
    },
    'garde-chez-eux': {
      description: message || '',
      animals: animals || [],
      dateTimeInstances: cartServiceValues?.['garde-chez-eux'] || [],
    },
    visite: { description: message || '', animals: animals || [], dateTimeInstances: [] }, // ← no preload
  })

  // User / profils
  const { currentUser } = useCurrentUser()
  const [petsitters, setPetsitters] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const queryClient = useQueryClient()

  // [HOOK] Floating Mobile Profile
  const floating = useFloatingProfile({
    defaultProfileKind: ProfileKind.PetSitting,
    publicProfile: !currentUser,
  })

  useEffect(() => {
    // Send gtm event when see new request page
    gtmSendCustomEvent('member_start_request_sst', {
      member_role_variable: 'maitre',
      member_id_variable: currentUser?.id || 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })
  }, [])

  useEffect(() => {
    const fetchPetsitters = async () => {
      if (petsittersIds.length === 0) {
        setPetsitters([])
        return
      }
      setIsLoading(true)
      try {
        const results = await Promise.all(petsittersIds.map((id) => getProfileAsync(id)))
        setPetsitters(results)
      } catch (e) {
        console.error('Error fetching petsitters:', e)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPetsitters()
  }, [petsittersIds])

  useEffect(() => {
    if (petsitters.length === 0) {
      setShowSelectedSittersModal(false)
    }
  }, [petsitters])

  // Description (debounce + modération)
  const [localDescription, setLocalDescription] = useState('')
  const debouncedDescription = useDebounce(localDescription, 500)

  const handleDescriptionChange = (value: string) => {
    setLocalDescription(value)
  }

  const handleDescriptionSubmit = () => {
    if (hasOffPlatformSignal(localDescription)) {
      setPendingDescription(localDescription)
      setShowSecurityModal(true)
      return false
    }
    return true
  }

  // mise à jour description → serviceValues(selected)
  const [currentServiceValues, setCurrentServiceValues] = useState<ServiceValues>({
    description: message || '',
    animals: animals || [],
    dateTimeInstances: [],
    allowAutoRebound: false,
    allowTenders: true,
  })

  useEffect(() => {
    if (debouncedDescription !== currentServiceValues?.description) {
      onFieldChange('description', debouncedDescription)
    }
  }, [debouncedDescription])

  useEffect(() => {
    setLocalDescription(currentServiceValues?.description || '')
  }, [selectedService])

  // click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactBoxRef.current && !contactBoxRef.current.contains(event.target as Node)) {
        setAddContactOpen(false)
        setIsEditing(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /* -------------------------- Filters & Pets -------------------------- */
  const filters: FiltersValues = {
    sortBy: 'recommender',
    address: currentUser?.settings?.address ?? null,
  }

  const pets = currentUser?.animals
    ? Object.values(currentUser.animals).map((animal) => ({
        id: animal.id,
        avatar: animal.mainPictureThumbnailUrl,
        displayName: animal.displayName,
        breedSlug: animal.breedSlug,
        icon: `emt-${animal.speciesSlug}`,
        selectedRadio: animal.speciesSlug,
      }))
    : []

  const hasSelectedDog = pets.some(
    (pet: any) => pet.selectedRadio === 'dog' && currentServiceValues?.animals?.includes(pet.id),
  )

  /* ---------------------- Recommended Query ---------------------- */
  const { data: dataSearchRecommended } = useQuery(['recommendedSearch', filters], () => getSearchAsync(filters))
  const recommendedPetsitters = dataSearchRecommended?.['hydra:member']?.map((profile: any) =>
    petSitterProfileToUserBoxDTO(profile),
  )
  const alreadyContactedPetsitters: any[] = []

  /* --------------------- Cart Data Query (local) --------------------- */
  const { data: cartData } = useQuery(['cart'], () => ({
    dateTimeInstances: selectedService ? (serviceValues as any)[selectedService]?.dateTimeInstances || [] : [],
    petsitters: petsittersIds,
    animals: currentServiceValues?.animals || [],
    message: '',
  }))

  // const isFirstRender = useRef(true)
  const isInitializing = useRef(true)

  // sync updateCart à chaque modif utile (sauf init)
  useEffect(() => {
    // if (isFirstRender.current) {
    //   isFirstRender.current = false
    //   return
    // }

    if (
      // !isInitializing.current &&
      selectedService &&
      getSlotType(selectedService)
    ) {
      const slug = getSlotType(selectedService)!
      const isRecurrent = slug === 'promenade' || slug === 'visite'
      const slots = formSlotsByService[slug] || []

      const rawInstances = slots.map((s) => s.data).filter(Boolean) as DateTimeInstance[]

      // let hasInvalidSlot = false
      const expandedAll: DateTimeInstance[] = []

      for (const slot of slots) {
        const ref = formRefs.current[slot.id]

        // if (ref?.validateOnly) {
        //   const isValid = ref.validateOnly()
        //   if (!isValid) {
        //     hasInvalidSlot = true
        //   }
        // }

        if (ref?.expandInstances) {
          const expanded = ref.expandInstances(isRecurrent ? slug : 'garde')
          expandedAll.push(...expanded)
        } else if (slot.data) {
          expandedAll.push(slot.data)
        }
      }

      const shouldSubmitInstances = (() => {
        // if (hasInvalidSlot) return false

        if (isRecurrent) {
          return expandedAll.length > 0
        }

        return !rawInstances.some((i) => isDateTimeInstanceIncomplete(i, slug)) && rawInstances.length > 0
      })()

      updateCart({
        service: selectedService,
        dateTimeInstances: shouldSubmitInstances ? (isRecurrent ? expandedAll : rawInstances) : [],
        petsitters: petsittersIds,
        animals: currentServiceValues?.animals || [],
        message: currentServiceValues?.description || '',
        allowAutoRebound: currentServiceValues?.allowAutoRebound ?? false,
        allowTenders: currentServiceValues?.allowTenders ?? true,
      })
    }
  }, [formSlotsByService, currentServiceValues, petsittersIds, selectedService])
  // }, [currentServiceValues, petsittersIds])

  useEffect(() => {
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!

    // If your store already contains a `summary` string, use it.
    const summary = (additionalInformations as any)?.summary ?? ''

    setServiceValues((prev) => {
      const prevService = prev[slug] as ServiceValues
      // avoid useless rerenders
      if ((prevService as any)?.additionalInfoSummary === summary) return prev

      return {
        ...prev,
        [slug]: {
          ...prevService,
          additionalInfoSummary: summary,
        },
      }
    })

    setCurrentServiceValues((prev) => {
      if ((prev as any)?.additionalInfoSummary === summary) return prev
      return { ...prev, additionalInfoSummary: summary }
    })
  }, [selectedService, (additionalInformations as any)?.summary])

  useEffect(() => {
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!

    const nextAnimals = animals || []

    setServiceValues((prev) => {
      const prevService = prev[slug] as ServiceValues
      // shallow compare (ids)
      const same =
        Array.isArray(prevService.animals) &&
        prevService.animals.length === nextAnimals.length &&
        prevService.animals.every((id, i) => id === nextAnimals[i])

      if (same) return prev

      return {
        ...prev,
        [slug]: {
          ...prevService,
          animals: nextAnimals,
        },
      }
    })

    setCurrentServiceValues((prev) => {
      const same =
        Array.isArray(prev.animals) &&
        prev.animals.length === nextAnimals.length &&
        prev.animals.every((id, i) => id === nextAnimals[i])

      if (same) return prev
      return { ...prev, animals: nextAnimals }
    })
  }, [selectedService, animals])

  // fin init
  useEffect(() => {
    if (cartData && dataSearchRecommended?.['hydra:member'] && isInitializing.current) {
      isInitializing.current = false
    }
  }, [cartData, dataSearchRecommended])

  /* --------------------------- Fields change --------------------------- */
  const onFieldChange = (key: string, value: any) => {
    if (!selectedService || !getSlotType(selectedService)) {
      setCurrentServiceValues((prev) => ({ ...prev, [key]: value }))
      return
    }
    const slug = getSlotType(selectedService)!
    const updated = {
      ...serviceValues,
      [slug]: { ...serviceValues[slug], [key]: value },
    }
    setServiceValues(updated)
    setCurrentServiceValues(updated[slug] as ServiceValues)

    if (formIsSend) {
      const msg = checkField(key, value)
      setErrors((prev) => ({ ...prev, [key]: msg }))
    }
  }

  useEffect(() => {
    if (selectedService && getSlotType(selectedService)) {
      const slug = getSlotType(selectedService)!
      setCurrentServiceValues(serviceValues[slug] as ServiceValues)
    } else {
      setCurrentServiceValues({ description: message || '', animals: animals || [], dateTimeInstances: [] })
    }
  }, [selectedService, serviceValues, animals, message])

  /* ----------------------------- Validation ---------------------------- */
  const checkForm = (): boolean => {
    let ok = true
    const newErrors: Record<string, string> = {}

    if (currentServiceValues) {
      Object.keys(currentServiceValues).forEach((key) => {
        const msg = checkField(key, (currentServiceValues as any)[key])
        if (msg) {
          ok = false
          newErrors[key] = msg
        }
      })
    }

    const contactError = checkField('contact', petsitters)
    if (contactError) {
      ok = false
      newErrors.contact = contactError
      contactBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // ✅ scroll vers animaux si erreur animaux (et seulement si pas déjà un scroll contact)
    if (!contactError && newErrors.animals) {
      animalsBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    setErrors((prev) => ({ ...prev, ...newErrors }))
    return ok
  }

  const getRejectedReason = (uuid?: string) => {
    if (!uuid) return undefined
    return rejectedPetsitters?.[uuid]?.reason as string | undefined
  }

  const isRejectedByDate = (petsitter?: { uuid?: string }) => {
    return getRejectedReason(petsitter?.uuid) === 'date'
  }

  const isRejectedByDailyQuota = (petsitter?: { uuid?: string }) => {
    return getRejectedReason(petsitter?.uuid) === 'daily-quota-exceeded'
  }

  const isRejectedByService = (petsitter?: { uuid?: string }, service?: string) => {
    return isServiceRejectedReason(getRejectedReason(petsitter?.uuid), service)
  }

  const hasServiceForRequest = (petsitter: any, service: string) => {
    return !isRejectedByService(petsitter, service)
  }

  const cannotReceiveRequestBecauseRejected = (petsitter?: { uuid?: string }, service?: string) => {
    return isRejectedByDate(petsitter) || isRejectedByDailyQuota(petsitter) || isRejectedByService(petsitter, service)
  }

  const canReceiveRequest = (petsitter: any, service: string) => {
    return (
      hasServiceForRequest(petsitter, service) && !isRejectedByDate(petsitter) && !isRejectedByDailyQuota(petsitter)
    )
  }

  const getValidPetsitters = (arr: any[], service: string) => {
    return arr.filter((p) => canReceiveRequest(p, service))
  }

  const scrollToSlot = (slotId: string) => {
    const ref = formRefs.current[slotId]
    if (ref?.container?.current) {
      ref.container.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  /* ------------------------------ Submit ------------------------------ */
  const onSubmit = async () => {
    setFormIsSend(true)
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!

    // 1) validation des sous-formulaires (si un validateOnly est fourni)

    setRequestSentInfo(buildRequestSentInfo())

    const validPetsitters = getValidPetsitters(petsitters, selectedService)

    if (petsitters.length === 0) {
      if (contactBoxRef?.current) {
        contactBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      setErrors((prev) => ({
        ...prev,
        contact: 'Merci de sélectionner au moins un pet sitter à contacter.',
      }))
      return
    } else {
      setErrors((prev) => ({
        ...prev,
        contact: '',
      }))
    }

    if (validPetsitters.length === 0) {
      if (contactBoxRef?.current) {
        contactBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    // const hasDailyQuotaExceeded = petsitters.some((p) => isRejectedByDailyQuota(p))
    // if (hasDailyQuotaExceeded) {
    //   if (contactBoxRef?.current) {
    //     contactBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    //   }
    //   return
    // }

    let allOK = true
    let firstErrorId: string | null = null
    for (const slot of formSlotsByService[slug]) {
      const ref = formRefs.current[slot.id]
      if (ref?.validateOnly) {
        const ok = ref.validateOnly()
        if (!ok) {
          allOK = false
          if (!firstErrorId) firstErrorId = slot.id
        }
      }
    }
    if (!allOK && firstErrorId) {
      scrollToSlot(firstErrorId)
      return
    }

    if (!checkForm()) return
    // 🔴 validation des infos complémentaires
    if (additionalInfosRef.current) {
      const ok = additionalInfosRef.current.validate()
      if (!ok) {
        additionalInfosRef.current.scrollToFirstError?.()
        return
      }
    }
    if (!handleDescriptionSubmit()) return

    // 2) expansion des instances via refs (recurrent/garde)
    const isRecurrent = slug === 'promenade' || slug === 'visite'
    const expandedAll: DateTimeInstance[] = []
    for (const slot of formSlotsByService[slug]) {
      const ref = formRefs.current[slot.id]
      if (ref?.expandInstances) {
        const expanded = ref.expandInstances(isRecurrent ? slug : 'garde')
        expandedAll.push(...expanded)
      } else if (slot.data) {
        expandedAll.push(slot.data)
      }
    }

    // 🧩 Construire la description finale
    const baseDescription = (currentServiceValues?.description || '').trim()
    const extra = ((currentServiceValues as any).additionalInfoSummary || '').trim()

    const finalDescription =
      extra.length > 0 ? `${baseDescription}${baseDescription ? '\n\n' : ''}${extra}` : baseDescription

    await updateCart({
      service: slug,
      dateTimeInstances: expandedAll,
      petsitters: petsittersIds,
      animals: currentServiceValues?.animals || [],
      message: finalDescription,
      allowAutoRebound: currentServiceValues?.allowAutoRebound ?? false,
      allowTenders: currentServiceValues?.allowTenders ?? true,
    })

    // if (validPetsitters.length < 5) {
    //   setSendMoreModalOpen(true)
    //   return
    // }

    setIsSubmitLoading(true)
    try {
      await checkoutCartAsync()
      setSubmittedContacts(validPetsitters)
      setRequestSentInfo(buildRequestSentInfo())
      clearForm()

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            request: 'sent',
            type: 'dogowner',
          },
        },
        undefined,
        { shallow: true },
      )

      setRequestSentModalOpen(true)

      // Send gtm event when send new request
      gtmSendCustomEvent('member_send_request_sst', {
        member_role_variable: 'maitre',
        member_id_variable: currentUser?.id || 'unknown',
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
      })
    } catch (e) {
      console.error('Checkout error:', e)
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const clearForm = () => {
    setServiceValues({
      promenade: { description: '', animals: [], dateTimeInstances: [] },
      'garde-chez-vous': { description: '', animals: [], dateTimeInstances: [] },
      'garde-chez-eux': { description: '', animals: [], dateTimeInstances: [] },
      visite: { description: '', animals: [], dateTimeInstances: [] },
    })
    setErrors({})
    setFormIsSend(false)

    // Les slots (dates) ne sont volontairement pas réinitialisés : ils font partie des paramètres
    // de recherche persistés, pour pouvoir contacter d'autres pet sitters sans tout ressaisir.
  }

  /* ---------------------------- Contacts handlers --------------------------- */
  const handleTogglePetsitter = (uuid: string) => {
    const isSelected = petsitters.some((c) => c.uuid === uuid)

    if (isSelected) {
      removePetsitter(uuid)
    } else {
      addPetsitter(uuid)

      // Send gtm event when a pet sitter is added to the selection
      gtmSendCustomEvent('member_add_to_selection_sst', {
        member_role_variable: 'maitre',
        member_id_variable: currentUser?.id || 'unknown',
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
      })
    }

    const updatedPetsitters = isSelected ? petsitters.filter((c) => c.uuid !== uuid) : [...petsitters, { uuid }]

    const contactError = checkField('contact', updatedPetsitters)
    setErrors((prev) => ({ ...prev, contact: contactError }))
  }

  const handleRemovePetsitterFromAvatar = (uuid: string) => {
    removePetsitter(uuid)
    queryClient.invalidateQueries(['cartPetsitters'])

    const updatedPetsitters = petsitters.filter((c) => c.uuid !== uuid)
    const contactError = checkField('contact', updatedPetsitters)
    setErrors((prev) => ({ ...prev, contact: contactError }))
  }

  const handleModifyClick = () => {
    if (isMobile) {
      setShowSelectedSittersModal(true)
    } else {
      setAddContactOpen((prev) => !prev)
      setIsEditing((prev) => !prev)
    }
  }

  /* ---------------------------- Animaux ---------------------------- */
  const handleAnimalSelection = (id: string) => {
    const next = currentServiceValues?.animals.includes(id)
      ? currentServiceValues?.animals.filter((i: string) => i !== id)
      : [...(currentServiceValues?.animals || []), id]
    setAnimals(next)
    const selectedPets = pets.filter((pet) => next.includes(pet.id))

    const counts: Record<'dog' | 'cat' | 'nac', number> = {
      dog: 0,
      cat: 0,
      nac: 0,
    }

    selectedPets.forEach((pet) => {
      counts[pet.selectedRadio as keyof typeof counts]++
    })

    Object.entries(counts).forEach(([slug, number]) => {
      updatePetToComplete(slug as 'dog' | 'cat' | 'nac', number)
    })

    onFieldChange('animals', next)
    if (next.length > 0) setErrors((prev) => ({ ...prev, animals: '' }))
  }

  /* ---------------------------- 🔁 SLOTS API ---------------------------- */

  const addNewSlot = () => {
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!
    addSlot(slug, makeBlankSlot(slug, null))
  }

  const handleSaveSlot = (slotId: string, instance: DateTimeInstance) => {
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!
    saveSlot(slug, slotId, instance)
  }

  const removeSlotUI = (slotId: string) => {
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!
    removeSlot(slug, slotId, (slug) => makeBlankSlot(slug, null))
  }

  // ensure au changement de service → au moins un slot existe
  const handleServiceChange = (value: string) => {
    setSelectedService(value)
    const slug = getSlotType(value)
    if (!slug) return
    const isRecurrent = slug === 'promenade' || slug === 'visite'
    // Compute the new value manually since setFormSlotsByService does not accept an updater function
    const prev = { ...formSlotsByService }
    let next = { ...prev }
    if (isRecurrent) {
      next = { ...prev, [slug]: [makeBlankSlot(slug, null)] }
    } else if ((prev[slug] ?? []).length === 0) {
      next = { ...prev, [slug]: [makeBlankSlot(slug, null)] }
    }
    setFormSlotsByService(next)
  }

  // dérivation “instances sauvegardées“ → currentServiceValues.dateTimeInstances (utile pour modals)
  useEffect(() => {
    if (!selectedService || !getSlotType(selectedService)) return
    const slug = getSlotType(selectedService)!
    const instances = (formSlotsByService[slug] || []).map((s) => s.data).filter(Boolean) as DateTimeInstance[]
    setServiceValues((prev) => ({
      ...prev,
      [slug]: { ...prev[slug], dateTimeInstances: instances },
    }))
  }, [formSlotsByService, selectedService])

  const [reviewsById, setReviewsById] = useState<Record<string, any>>({})

  // Fetch reviews when profile changes

  const fetchReviews = async (id) => {
    if (id) {
      try {
        const reviewsData = await getReviews(id, {
          page: 1,
          perPage: 10,
          sortBy: 'date',
          direction: 'desc',
        })
        return {
          reviews: reviewsData.lastReviews.map((r) => ({
            ...r,
            comment: r.content,
            reviewDate: r.createdAt,
            displayName: r.author,
            rating: r.rating,
            thumbnailUrl: r.authorAvatarUrl || '',
          })),
          averageRating: reviewsData.averageRating,
          reviewsCount: reviewsData.totalReviewsCount,
        }
      } catch (e) {
        // ignore reviews error
      }
    }
  }

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const entries = await Promise.all(
        petsitters.map(async (p) => {
          const data = await fetchReviews(p.uuid)
          return [p.uuid, data] as const
        }),
      )

      if (!cancelled) {
        setReviewsById(Object.fromEntries(entries))
      }
    }

    if (petsitters.length) run()
    return () => {
      cancelled = true
    }
  }, [petsitters])

  const buildRequestSentInfo = () => {
    if (!petsitters?.length || !selectedService) return null

    const withoutService = petsitters.filter((p) => !hasServiceForRequest(p, selectedService))

    const unavailableOnDates = petsitters.filter((p) => rejectedPetsitters?.[p.uuid]?.reason === 'date')

    const overContacted = petsitters.filter((p) => rejectedPetsitters?.[p.uuid]?.reason === 'daily-quota-exceeded')

    return (
      <>
        {/* SERVICE INFO */}
        {withoutService.length > 0 && (
          <InfoTextBox
            infoText={
              <EmtTypography variant="body2">
                <b>
                  {withoutService.map((p, i) =>
                    i === 0 ? p.name : i === withoutService.length - 1 ? ` et ${p.name}` : `, ${p.name}`,
                  )}
                </b>{' '}
                n&apos;effectue{withoutService.length > 1 ? 'nt' : ''} pas de{' '}
                <b>{SERVICE_NAME_MAP[selectedService as keyof typeof SERVICE_NAME_MAP] || selectedService}</b>, votre
                demande ne {withoutService.length > 1 ? 'leur' : 'lui'} a pas été envoyée.{' '}
                <b>Les autres pet sitters sélectionnés ont bien été contactés.</b>
              </EmtTypography>
            }
          />
        )}

        {/* DATE INFO */}
        {unavailableOnDates.length > 0 && (
          <InfoTextBox
            infoText={
              <EmtTypography variant="body2">
                <b>
                  {unavailableOnDates.map((p, i) =>
                    i === 0 ? p.name : i === unavailableOnDates.length - 1 ? ` et ${p.name}` : `, ${p.name}`,
                  )}
                </b>{' '}
                n&apos;est pas disponible aux dates sélectionnées, votre demande ne{' '}
                {unavailableOnDates.length > 1 ? 'leur' : 'lui'} a pas été envoyée.{' '}
                <b>Les autres pet sitters sélectionnés ont bien été contactés.</b>
              </EmtTypography>
            }
          />
        )}

        {/* DAILY QUOTA INFO */}
        {overContacted.length > 0 && (
          <InfoTextBox
            infoText={
              <EmtTypography variant="body2">
                Votre demande n&apos;est pas envoyée à{' '}
                <b>
                  {overContacted.map((p, i) =>
                    i === 0 ? p.name : i === overContacted.length - 1 ? ` et ${p.name}` : `, ${p.name}`,
                  )}
                </b>{' '}
                car vous avez atteint le nombre limite de demandes journalières (20).{' '}
                <b>Les autres pet sitters sélectionnés ont bien été contactés.</b>
              </EmtTypography>
            }
          />
        )}
      </>
    )
  }

  /* ------------------------------- JSX ------------------------------- */
  return (
    <Container
      sx={{
        paddingTop: isMobile ? '25px' : '30px',
        paddingBottom: isMobile ? '200px' : 0,
        display: 'flex',
        flexDirection: 'column',
        rowGap: '30px',
        borderRadius: isMobile ? '18px 18px 0 0' : '0',
        marginTop: isMobile ? '-18px' : '0',
        position: 'relative',
        backgroundColor: isMobile ? '#fff' : '#F6F6F6',
        minHeight: isMobile ? '100vh' : 'auto',
      }}
      maxWidth="lg"
    >
      <Box
        sx={{
          marginTop: isMobile ? '0' : '-200px!important',
          backgroundColor: '#F6F6F6',
          borderRadius: '18px',
          padding: isMobile ? '0' : '20px 15px',
        }}
      >
        <EmtGridContainer columnSpacing={{ xs: '25px', lg: '25px' }} sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Grid
            item
            md={isTablet ? 8 : 8}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', rowGap: isMobile ? '30px' : '40px' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '15px' : '30px',
                padding: isMobile ? '0' : '20px 15px',
                backgroundColor: '#fff',
                borderRadius: '18px',
              }}
            >
              {/* Contact Selector */}
              <Box>
                <Box
                  ref={contactBoxRef}
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  paddingBottom="20px"
                  position="relative"
                  gap="10px"
                  sx={{ flexShrink: 0 }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="16px"
                    minHeight="40px"
                    sx={{ maxWidth: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
                  >
                    <EmtTypography variant="body2" color="#A5A3A9" sx={{ flexShrink: 0 }}>
                      À :
                    </EmtTypography>
                    <>
                      <Box
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          flexWrap: isMobile ? 'nowrap' : 'wrap',
                          gap: '15px',
                          overflowX: 'auto',
                          padding: '0 2px',
                        }}
                      >
                        {petsitters?.map((petsitter) => {
                          const hasService = hasServiceForRequest(petsitter, selectedService || '')
                          const isUnavailableForRequest = cannotReceiveRequestBecauseRejected(
                            petsitter,
                            selectedService || '',
                          )

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <PetsitterAvatar
                                key={petsitter.uuid}
                                uuid={petsitter.uuid}
                                name={petsitter.name}
                                mainPicture={petsitter.mainPicture}
                                isEditing={isEditing}
                                hasService={hasService && !isUnavailableForRequest}
                                onRemove={() => handleRemovePetsitterFromAvatar(petsitter.uuid)}
                                onOpenProfile={() => {
                                  floating.handleOpenProfile(petsitter.uuid)
                                }}
                              />
                              {isMobile && <PetsitterPrice uuid={petsitter.uuid} />}
                            </Box>
                          )
                        })}
                        {Array.from({ length: 10 - (petsitters?.length || 0) }).map((_, index) => (
                          <EmptyPetsitterSlot
                            key={`empty-${index}`}
                            isLoading={isLoading && index < petsittersIds.length - (petsitters?.length || 0)}
                          />
                        ))}
                      </Box>
                    </>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'row' : 'column',
                      alignItems: isMobile ? 'flex-start' : 'flex-end',
                      gap: '10px',
                      flexShrink: 0,
                      position: isMobile ? 'absolute' : 'relative',
                      top: isMobile ? '0' : 'initial',
                      right: isMobile ? '0' : 'initial',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap="16px" minHeight={isMobile ? 'initial' : '40px'}>
                      <EmtTypography variant="body2" color="#A5A3A9" sx={{ lineHeight: '1.1' }}>
                        {petsitters?.filter((p) => canReceiveRequest(p, selectedService || '')).length ?? 0}/10
                      </EmtTypography>
                    </Box>
                    {(petsitters?.length > 0 || !isMobile) && (
                      <EmtTypography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: '#AE59C5',
                          lineHeight: '1.1',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={handleModifyClick}
                      >
                        {isEditing ? 'Enregistrer' : 'Modifier'}
                      </EmtTypography>
                    )}
                  </Box>

                  {addContactOpen && recommendedPetsitters && (
                    <Box
                      sx={{
                        width: '100%',
                        position: 'absolute',
                        top: 'calc(100% + 15px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        zIndex: 2,
                        backgroundColor: '#fff',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            height: '48px',
                            width: '100%',
                            backgroundColor: contactList === 'recommanded' ? '#AE59C6' : '#F9F9F9',
                            color: contactList === 'recommanded' ? '#fff' : '#1D1929',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                          }}
                          onClick={() => setContactList('recommanded')}
                        >
                          <EmtIcon className="emt-star" />
                          <EmtTypography variant="body2" fontWeight="700">
                            {t('reservations.newRequests.contactSections.recommanded')}
                          </EmtTypography>
                        </Box>
                        {/* <Box
                          sx={{
                            height: '48px',
                            width: '100%',
                            backgroundColor: contactList === 'already-contacted' ? '#AE59C6' : '#F9F9F9',
                            color: contactList === 'already-contacted' ? '#fff' : '#1D1929',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                          }}
                          onClick={() => setContactList('already-contacted')}
                        >
                          <EmtIcon className="emt-users" />
                          <EmtTypography variant="body2" fontWeight="700">
                            {t('reservations.newRequests.contactSections.alreadyContacted')}
                          </EmtTypography>
                        </Box> */}
                      </Box>

                      <Box
                        sx={{
                          backgroundColor: '#fff',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          borderRadius: '16px',
                          boxShadow: '0px 4px 8px 0px #00000014',
                        }}
                      >
                        {contactList === 'recommanded'
                          ? recommendedPetsitters?.map((petsitter: any, index: number) => (
                              <UserBox
                                key={`recommanded-${index}`}
                                data={petsitter}
                                onClick={() => handleTogglePetsitter(petsitter.uuid)}
                                isSelected={petsitters.some((c) => c.uuid === petsitter.uuid)}
                                checkbox
                              />
                            ))
                          : alreadyContactedPetsitters?.map((petsitter: any, index: number) => (
                              <UserBox
                                key={`already-contacted-${index}`}
                                data={petsitter}
                                onClick={() => handleTogglePetsitter(petsitter.uuid)}
                                isSelected={petsitters.some((c) => c.uuid === petsitter.uuid)}
                                checkbox
                              />
                            ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* {errors.contact && (
                  <EmtTypography
                    variant="body2"
                    fontSize="0.75rem"
                    color="error"
                    paddingTop="5px"
                    letterSpacing="0.03333em"
                  >
                    {errors.contact}
                  </EmtTypography>
                )} */}

                {(petsitters?.length === 0 || errors.contact) && (
                  <InfoTextBox
                    infoText={
                      <EmtTypography variant="body2">
                        Sélectionnez des pet sitters pour pouvoir effectuer une demande de réservation. <br />
                        <Link href={Routes.PetSitter.Search} passHref>
                          <EmtTypography
                            variant="body2"
                            color="purple.main"
                            fontWeight="600"
                            sx={{ textDecoration: 'underline', cursor: 'pointer', display: 'inline' }}
                          >
                            Voir les pet sitters
                          </EmtTypography>
                        </Link>
                      </EmtTypography>
                    }
                  />
                )}

                {/* Info sur services manquants */}
                {petsitters?.length > 0 && selectedService && (
                  <>
                    {(() => {
                      const without = petsitters.filter((p) => !hasServiceForRequest(p, selectedService))
                      if (without.length === 0) return null
                      if (without.length === petsitters.length) {
                        return (
                          <InfoTextBox
                            infoText={
                              <EmtTypography variant="body2">
                                Aucun des pet sitters sélectionnés n&apos;effectuent de{' '}
                                <b>
                                  {SERVICE_NAME_MAP[selectedService as keyof typeof SERVICE_NAME_MAP] ||
                                    selectedService}
                                  .
                                </b>
                                <br />
                                Veuillez sélectionner un service différent ou bien{' '}
                                <Link href={Routes.PetSitter.Search} passHref>
                                  <EmtTypography
                                    variant="body2"
                                    color="purple.main"
                                    fontWeight="600"
                                    sx={{ textDecoration: 'underline', cursor: 'pointer', display: 'inline' }}
                                  >
                                    ajouter de nouveaux pet sitters à votre sélection.
                                  </EmtTypography>
                                </Link>
                              </EmtTypography>
                            }
                          />
                        )
                      }
                      return (
                        <InfoTextBox
                          infoText={
                            <EmtTypography variant="body2">
                              <b>
                                {without.map((p, i) =>
                                  i === 0 ? p.name : i === without.length - 1 ? ` et ${p.name}` : `, ${p.name}`,
                                )}
                              </b>{' '}
                              n&apos;effectue{without.length > 1 ? 'nt' : ''} pas de{' '}
                              <b>
                                {SERVICE_NAME_MAP[selectedService as keyof typeof SERVICE_NAME_MAP] || selectedService}
                              </b>
                              , {without.length > 1 ? 'votre demande ne leur' : 'votre demande ne lui'} sera pas envoyée
                            </EmtTypography>
                          }
                        />
                      )
                    })()}
                  </>
                )}

                {/* Info sur dates indisponibles */}
                {petsitters?.length > 0 && selectedService && (
                  <>
                    {(() => {
                      const unavailableOnDates = petsitters.filter(
                        (p) => rejectedPetsitters?.[p.uuid]?.reason === 'date',
                      )

                      if (unavailableOnDates.length === 0) return null

                      if (unavailableOnDates.length === petsitters.length) {
                        return (
                          <InfoTextBox
                            infoText={
                              <EmtTypography variant="body2">
                                Aucun des pet sitters sélectionnés n&apos;est disponible aux dates sélectionnées.
                                <br />
                                Veuillez modifier vos dates ou bien{' '}
                                <Link href={Routes.PetSitter.Search} passHref>
                                  <EmtTypography
                                    variant="body2"
                                    color="purple.main"
                                    fontWeight="600"
                                    sx={{ textDecoration: 'underline', cursor: 'pointer', display: 'inline' }}
                                  >
                                    ajouter de nouveaux pet sitters à votre sélection.
                                  </EmtTypography>
                                </Link>
                              </EmtTypography>
                            }
                          />
                        )
                      }

                      return (
                        <InfoTextBox
                          infoText={
                            <EmtTypography variant="body2">
                              <b>
                                {unavailableOnDates.map((p, i) =>
                                  i === 0
                                    ? p.name
                                    : i === unavailableOnDates.length - 1
                                    ? ` et ${p.name}`
                                    : `, ${p.name}`,
                                )}
                              </b>{' '}
                              n&apos;{unavailableOnDates.length > 1 ? 'sont' : 'est'} pas disponible
                              {unavailableOnDates.length > 1 ? 's' : ''} aux dates sélectionnées,{' '}
                              {unavailableOnDates.length > 1 ? 'votre demande ne leur' : 'votre demande ne lui'} sera
                              pas envoyée.
                            </EmtTypography>
                          }
                        />
                      )
                    })()}
                  </>
                )}

                {/* Info sur daily quota exceeded */}
                {petsitters?.length > 0 && selectedService && (
                  <>
                    {(() => {
                      const overDailyQuota = petsitters.filter((p) => isRejectedByDailyQuota(p))

                      if (overDailyQuota.length === 0) return null

                      return (
                        <InfoTextBox
                          infoText={
                            <EmtTypography variant="body2">
                              Certains des pet sitters sélectionnés ne seront pas contactés, car vous ne pouvez faire
                              que 20 contacts par jour. Vous pourrez faire de nouveaux contacts à partir de minuit.
                            </EmtTypography>
                          }
                        />
                      )
                    })()}
                  </>
                )}
              </Box>
              {/* Contact Selector */}

              {/* Service Navigation */}
              <Box
                sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: isMobile ? '15px' : '30px' }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#fff',
                    opacity: '0.7',
                    display: addContactOpen ? 'initial' : 'none',
                  }}
                />
                <EmtTypography variant="body1" fontWeight="700" sx={{ marginBottom: isMobile ? '0' : '-14px' }}>
                  Confirmez le service et les dates :
                </EmtTypography>

                {/* Service Content */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '20px' : '30px' }}>
                  <Box>
                    <ServiceSection
                      isMobile={isMobile}
                      selectedService={selectedService}
                      errors={errors}
                      formSlotsByService={formSlotsByService}
                      formRefs={formRefs}
                      localStorageInitialValues={localStorageInitialValues}
                      onServiceChange={handleServiceChange}
                      onAddNewSlot={addNewSlot}
                      onSaveSlot={handleSaveSlot}
                      onRemoveSlot={removeSlotUI}
                      showTitle={false}
                    />
                    {errors.dateTimeInstances && (
                      <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                        {errors.dateTimeInstances}
                      </EmtTypography>
                    )}
                  </Box>

                  {(!isMobile || selectedService) && (
                    <>
                      {/* Animals */}
                      <Box ref={animalsBoxRef} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <EmtTypography variant="body1" fontWeight="700">
                          {t('reservations.newRequests.labels.animalsSection')}
                        </EmtTypography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(pets.length || 0, 3)}, 1fr)`,
                            gap: '10px',
                          }}
                        >
                          {pets.map((pet, index) => (
                            <AnimalBox
                              key={`animal-${index}`}
                              data={pet as any}
                              isSelected={currentServiceValues?.animals?.includes((pet as any).id)}
                              onClick={() => handleAnimalSelection((pet as any).id)}
                              checkbox
                            />
                          ))}
                        </Box>
                        {errors.animals && (
                          <EmtTypography
                            variant="body2"
                            fontSize="0.75rem"
                            color="error"
                            paddingTop="5px"
                            letterSpacing="0.03333em"
                          >
                            {errors.animals}
                          </EmtTypography>
                        )}
                        <AddAnimalModal
                          open={addModalOpen}
                          close={() => setAddModalOpen(false)}
                          defaultImages={true}
                          defaultDescription={true}
                        />

                        <Box
                          onClick={() => setAddModalOpen(true)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px',
                            padding: ' 10px 18px',
                            border: '2px dashed #AE59C6',
                            borderRadius: '16px',
                            cursor: 'pointer',
                            backgroundColor: '#F7EEF9',
                          }}
                        >
                          {/* Circle with plus icon */}
                          <Box
                            sx={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#fff',
                              border: '1px solid #F9F9F9',
                              boxShadow: '0px 4px 56px 0px #00000014, 0px 12px 16px 0px #00000014',
                            }}
                          >
                            <EmtIcon
                              className="emt-add-small"
                              size="medium"
                              sx={{ fontSize: '34px', color: '#AE59C6' }}
                            />
                          </Box>

                          {/* Label text */}
                          <EmtTypography variant="body1" fontWeight="700" color="#AE59C6">
                            Ajouter un nouvel animal
                          </EmtTypography>
                        </Box>
                      </Box>

                      {/* Caractéristiques */}
                      <AdditionalInformations
                        ref={additionalInfosRef}
                        values={additionalInformations}
                        setValues={setAdditionalInformations}
                        service={selectedService || ''}
                        hasSelectedDog={hasSelectedDog}
                        onChange={(patch) => {
                          if (!selectedService || !getSlotType(selectedService)) return
                          const slug = getSlotType(selectedService)!

                          setServiceValues((prev) => {
                            const prevService = prev[slug] as ServiceValues
                            return {
                              ...prev,
                              [slug]: {
                                ...prevService,
                                additionalInfoSummary:
                                  (patch as any).summary ?? (prevService as any).additionalInfoSummary ?? '',
                              },
                            }
                          })

                          setCurrentServiceValues((prev) => ({
                            ...prev,
                            additionalInfoSummary: (patch as any).summary ?? (prev as any).additionalInfoSummary ?? '',
                          }))
                        }}
                      />
                      {/* END Caractéristiques */}

                      {/* Description */}
                      <Box>
                        <EmtTypography variant="body1" fontWeight="700" sx={{ marginBottom: '10px' }}>
                          {t('reservations.newRequests.labels.additionalInfo')}
                          <span style={{ fontWeight: '400', fontSize: '14px', color: '#77757F' }}> (Optionnel)</span>
                        </EmtTypography>
                        <EmtTextarea
                          hideLabelOnClick
                          maxLength={1000}
                          sx={{ height: '110px', paddingRight: '70px' }}
                          label="Expliquez au(x) pet sitter(s) votre demande en quelques mots."
                          value={localDescription}
                          onChange={handleDescriptionChange}
                          error={!!errors.description}
                          helperText={errors.description}
                        />
                      </Box>

                      {/* Auto rebound */}
                      {/* <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <EmtCheckbox
                          variant="purple"
                          size="small"
                          checked={currentServiceValues?.allowAutoRebound ?? true}
                          onChange={(e) => onFieldChange('allowAutoRebound', (e.target as HTMLInputElement).checked)}
                        />
                        <EmtTypography variant="body2">
                          <b>
                            Si les pet sitters que j’ai choisis ne peuvent accepter ma demande, j’aimerais que Super
                            Sitters me mette en contact avec d’autres profils de confiance.
                          </b>
                        </EmtTypography>
                      </Box> */}

                      {/* Allow tenders */}
                      {/* <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <EmtCheckbox
                          variant="purple"
                          size="small"
                          checked={currentServiceValues?.allowTenders ?? true}
                          onChange={(e) => onFieldChange('allowTenders', (e.target as HTMLInputElement).checked)}
                        />
                        <EmtTypography variant="body2">
                          <b>
                            Si les pet sitters que j’ai choisis ne peuvent accepter ma demande, j’aimerais que Super
                            Sitters me mette en contact avec d’autres profils de confiance.
                          </b>
                        </EmtTypography>
                      </Box> */}

                      {/* Submit */}
                      <Box
                        sx={{
                          position: isMobile ? 'fixed' : 'initial',
                          bottom: '30px',
                          left: 0,
                          zIndex: isMobile ? 999 : 'initial',
                          width: '100%',
                          padding: isMobile ? '0 15px' : '0',
                        }}
                        className="EmtReactNativeWebViewPaddingBottom"
                      >
                        <EmtButton
                          fullWidth
                          startIcon={
                            <EmtIcon className="emt-message-text-alt" sx={{ fontSize: '20px', marginTop: '2px' }} />
                          }
                          loader={isSubmitLoading}
                          size="medium"
                          color="purple"
                          onClick={onSubmit}
                        >
                          {t('reservations.newRequests.labels.sendRequest')}&nbsp;
                          <span style={{ fontWeight: '400' }}>
                            ({t('reservations.newRequests.labels.noEngagement')})
                          </span>
                        </EmtButton>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>

          {!isMobile && (
            <Grid
              item
              md={isTablet ? 4 : 4}
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', rowGap: '20px', position: 'relative' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px', height: '100%' }}>
                <EmtTypography variant="body1" fontWeight="700" fontSize="18px">
                  {t('reservations.newRequests.reservation.reservationWith')}
                </EmtTypography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '15px',
                      flexGrow: '1',
                      minHeight: '100%',
                      height: '0!important',
                    }}
                  >
                    {petsitters.map((contact, index) => {
                      const rs = reviewsById[contact.uuid]
                      return (
                        <AskSitterCard
                          data={{
                            ...contact,
                            reviewsCount: rs?.reviewsCount ?? 0,
                            reviewsRating: rs?.averageRating ?? 0,
                          }}
                          variant="view"
                          rejectedReason={rejectedPetsitters?.[contact.uuid]?.reason}
                        />
                      )
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </EmtGridContainer>
      </Box>

      <RequestSentModal
        open={requestSentModalOpen}
        setOpen={setRequestSentModalOpen}
        onClickButton={() => router.push(Routes.PetSitter.Reservations.Base)}
        serviceKey={selectedService || ''}
        service={selectedService ? services.find((s) => s.slug === selectedService)?.title : ''}
        leftIconClassName={selectedService ? services.find((s) => s.slug === selectedService)?.iconClass : ''}
        leftIcon={
          selectedService
            ? (services.find((s) => s.slug === selectedService) as any)?.icon?.({
                color: 'currentColor',
              }) || null
            : null
        }
        dateTimeInstances={
          selectedService && getSlotType(selectedService)
            ? ((formSlotsByService[getSlotType(selectedService)!] || [])
                .map((s) => s.data)
                .filter(Boolean) as DateTimeInstance[])
            : []
        }
        title={t('reservations.newRequests.modals.requestSentTitle') as string}
        info={requestSentInfo}
        subtitle={
          t('reservations.newRequests.modals.requestSentSubtitle', {
            contactName: submittedContacts[0]?.name,
          }) as string
        }
      />

      <SendMoreModal
        open={sendMoreModalOpen}
        selectedService={selectedService || ''}
        setOpen={setSendMoreModalOpen}
        isSubmitLoading={isSubmitLoading}
        onSend={() => {
          setIsSubmitLoading(true)
          checkoutCartAsync()
            .then(() => {
              clearForm()
              setSendMoreModalOpen(false)
              setRequestSentModalOpen(true)
            })
            .catch((e) => console.error('Checkout error:', e))
            .finally(() => setIsSubmitLoading(false))
        }}
        recommendedPetsitters={(recommendedPetsitters ?? []).filter((p) => checkHasService(p, selectedService || ''))}
        handleOpenProfile={handleOpenProfile}
      />

      <SecurityModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        pro={false}
        pendingMessage={pendingDescription}
        onModifyMessage={(message) => setLocalDescription(message)}
      />

      <EmtModal fullScreenMobile open={showSelectedSittersModal} onClose={() => setShowSelectedSittersModal(false)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              flexGrow: '1',
              minHeight: '100%',
              paddingTop: '30px',
            }}
          >
            <EmtTypography variant="body1" fontWeight="700" fontSize="18px">
              Votre demande sera envoyée à :
            </EmtTypography>
            {petsitters.map((contact, index) => {
              const rs = reviewsById[contact.uuid]
              return (
                <AskSitterCard
                  data={{
                    ...contact,
                    reviewsCount: rs?.reviewsCount ?? 0,
                    reviewsRating: rs?.averageRating ?? 0,
                  }}
                  variant="view"
                  rejectedReason={rejectedPetsitters?.[contact.uuid]?.reason}
                />
              )
            })}
          </Box>
        </Box>
      </EmtModal>
      {/* MOBILE PROFILE MODAL */}
      <FloatingProfileModal
        isMobile={floating.isMobile}
        open={floating.profileIsOpen}
        customerId={floating.customerId}
        profileKind={floating.profileKind}
        onClose={floating.handleCloseProfile}
      />
    </Container>
  )
}

NewRequest.displayName = 'NewRequest'
export default NewRequest

/* -------------------------------- Utils -------------------------------- */

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debouncedValue
}
